import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { projectsApi } from '../api/projects';
import { documentImportApi } from '../api/documentImport';
import { applyImportNext } from '../components/DocumentImport/importNext';
import SeriesNotes from '../components/Series/SeriesNotes';
import BookToolsGroup from '../components/BookTools/BookToolsGroup';
import { projectNotesPath } from '../utils/seriesNotes';
import './SeriesBookBoardPage.css';

/**
 * Series Docs door (#672 / #823). Timeline/Docs toggle. Same
 * Document Import v1 pile as Book Planning Docs. Not a book
 * mode-switch. A series visit clears the selected book so the
 * left panel stays Series Planning.
 */
export default function ProjectNotesPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, currentStory, setCurrentProject, setCurrentStory } = useApp();

  const [project, setProject] = useState(
    currentProject?.id === projectId ? currentProject : null,
  );
  const [importBatches, setImportBatches] = useState([]);
  const [importsLoading, setImportsLoading] = useState(true);
  const [importsError, setImportsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectId) return;
      if (currentProject?.id === projectId) {
        setProject(currentProject);
        if (currentProject.is_series) setCurrentStory?.(null);
        return;
      }
      try {
        const next = await projectsApi.get(projectId);
        if (!cancelled && next) {
          setProject(next);
          setCurrentProject(next);
          if (next.is_series) setCurrentStory?.(null);
        }
      } catch {
        if (!cancelled) setProject(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, currentProject, setCurrentProject, setCurrentStory]);

  const loadImports = useCallback(async () => {
    if (!projectId) return;
    setImportsLoading(true);
    setImportsError(null);
    try {
      const data = await documentImportApi.list(projectId);
      setImportBatches(data?.batches || []);
    } catch (err) {
        setImportsError(err.message || 'Could not load docs.');
      setImportBatches([]);
    } finally {
      setImportsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadImports();
  }, [loadImports]);

  const isSeries = Boolean(project?.is_series);
  const projectName = project?.name || 'Docs';

  return (
    <div className="sbb-page">
      <header className="sbb-page__intro">
        {isSeries ? (
          <>
            <p className="sbb-kicker">Series</p>
            <div className="sbb-title-row">
              <h1 className="sbb-page__title">{projectName}</h1>
              <BookToolsGroup />
            </div>
            <nav className="sbb-switch" aria-label="Series room">
              <button
                type="button"
                className="sbb-switch__btn"
                onClick={() => navigate(`/projects/${projectId}/series`)}
              >
                Timeline
              </button>
              <button
                type="button"
                className="sbb-switch__btn is-current"
                aria-current="page"
                onClick={() => navigate(projectNotesPath(projectId))}
              >
                Docs
              </button>
            </nav>
          </>
        ) : (
          <p className="sbb-kicker">Project</p>
        )}
      </header>

      <SeriesNotes
        bookTools={Boolean(project) && !isSeries}
        project={project || { id: projectId }}
        batches={importBatches}
        loading={importsLoading}
        error={importsError}
        onRefresh={loadImports}
        onImportNext={(payload) => applyImportNext(navigate, payload, project, {
          currentStoryId: currentStory?.id,
        })}
      />
    </div>
  );
}
